import { useState, useContext, useEffect } from 'react'
import { styled } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid2'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useParams, useNavigate } from 'react-router-dom'
import { useFormContext } from 'react-hook-form'

import { createUniqueDeepClone, useSessionTypeOptions, useTagsDataOptions } from '../../../../utils'
import { SubEvents, Sessions } from '../../../../types'
import type { ModifySubEventsSchema } from '../../../events/Events.schema'
import SubEventForm from './components/SubEventForm'
import { computeSessionId, defaultSubEvent } from './components/SubEvents.utils'
import './components/SessionForm'
import SessionForm from './components/SessionForm'
import { SubEventsStep2Context } from './SubEventsStep2.Context'
import { StatusTag } from '../../../root/components/StatusTag'

const Span = styled('span')<{ lineHeight?: number; bold?: boolean }>(
  ({ theme, lineHeight, bold }) => ({
    color: theme.palette.text.secondary,
    lineHeight: lineHeight ?? undefined,
    fontWeight: bold ? 'bold' : 'normal',
    flex: 1,
    textAlign: 'start',
  })
)
const Card = styled('button')(({ theme }) => ({
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '1rem',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  width: '100%',
  height: '100%',
}))

interface InnerProps {
  id: number
  sessionKey: number
  minDateTime: Date | undefined
  maxDateTime: Date | undefined
  onRemove: () => void
}

function InnerSessionList({ sessionKey, id, minDateTime, maxDateTime, onRemove }: InnerProps) {
  const { t } = useTranslation('events')
  const { eventId } = useParams()
  const navigate = useNavigate()

  const {
    subEventsFields,
    eventLocation,
    removeSubEvent,
    appendSubEvent,
    updateSubEvent,
    appendSession,
    updateSession,
    setShowSearch,
  } = useContext(SubEventsStep2Context)
  const { watch, setValue, getValues } = useFormContext<ModifySubEventsSchema>()

  const sessionId = watch(`sessions.${sessionKey}.id`)

  const subEventsIterator: [number, SubEvents][] = subEventsFields.reduce(
    (acc, [key, subEvent]) => {
      if (sessionKey === -1) {
        if (!subEvent.sessions.length) {
          acc.push([key, subEvent])
        }
      } else if (subEvent.sessions.includes(sessionId)) {
        acc.push([key, subEvent])
      }
      return acc
    },
    [] as [number, SubEvents][]
  )

  return (
    <Stack
      spacing={1}
      paddingX={{ sx: 1, sm: 2, md: 3 }}
      alignItems="center"
      flex="1 0"
      width="100%"
      id={computeSessionId(id)}
    >
      {sessionKey === -1 ? (
        <Stack width="100%">
          <h5>{t('Sans séance')}</h5>
          <Stack
            spacing={1}
            padding={2}
            sx={theme => ({
              backgroundColor: theme.colors.greyBackground,
              borderRadius: '0 0 1rem 1rem',
              borderTop: `0.15rem solid ${theme.colors.secondary}`,
            })}
          />
        </Stack>
      ) : (
        <SessionForm
          location={eventLocation}
          sessionKey={sessionKey}
          minDateTime={minDateTime}
          maxDateTime={maxDateTime}
          onRemove={() => {
            onRemove()
            subEventsIterator.forEach(([key]) => {
              const ids = getValues(`subEvents.${key}.sessions`)
              ids.splice(ids.indexOf(sessionId), 1)
              setValue(`subEvents.${key}.sessions`, ids)
              updateSubEvent(key, { ...getValues(`subEvents.${key}`), sessions: ids })
            })
          }}
          onDuplicate={data => {
            const newSession = createUniqueDeepClone(data)
            newSession.sub_events = []
            appendSession(newSession)
            navigate(
              `/admin/events/${eventId}/sessions?viewType=dashboard&sessionId=${newSession.id}`
            )
            setShowSearch(false)
          }}
        />
      )}

      <Stack
        spacing={1}
        padding={2}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={theme => ({
          backgroundColor: theme.colors.secondaryBackground,
          borderRadius: '1rem',
        })}
        width={{ xs: '95%', sm: '85%' }}
      >
        <Span bold>{t('Détails des interventions')} :</Span>
        <Span>
          {subEventsIterator.length ?? 0} {t('intervention(s)')}
        </Span>
      </Stack>
      <Stack spacing={2} alignItems="center" width="100%">
        {subEventsIterator.map(([key, subEvent]) => (
          <SubEventForm
            key={subEvent.id}
            id={subEvent.id}
            subEventKey={key}
            minDateTime={minDateTime}
            maxDateTime={maxDateTime}
            location={eventLocation}
            onUpdateSession={(reason, details) => {
              if (reason === 'selectOption' && details?.option) {
                const sessions = getValues('sessions')
                const sessionKey = sessions.findIndex(session => session.id === details.option.id)
                const ids = (getValues(`sessions.${sessionKey}.sub_events`) ?? []).concat(
                  details.option.id
                )
                setValue(`sessions.${sessionKey}.sub_events`, ids)
                updateSession(sessionKey, {
                  ...sessions[sessionKey],
                  sub_events: ids,
                })
              }
              if (reason === 'removeOption' && details?.option) {
                const sessions = getValues('sessions')
                const sessionKey = sessions.findIndex(session => session.id === details.option.id)
                const ids = (getValues(`sessions.${sessionKey}.sub_events`) ?? []).filter(
                  id => id !== details.option.id
                )
                setValue(`sessions.${sessionKey}.sub_events`, ids)
                updateSession(sessionKey, {
                  ...sessions[sessionKey],
                  sub_events: ids,
                })
              }
            }}
            onRemove={() => {
              removeSubEvent(key)
            }}
            onDuplicate={data => {
              const newSubEvent = createUniqueDeepClone(data)
              newSubEvent.sessions = []
              appendSubEvent(newSubEvent)
              navigate(
                `/admin/events/${eventId}/sessions/subEvent/${newSubEvent.id}?viewType=dashboard`
              )
              setShowSearch(false)
            }}
            backgroundColor="secondaryBackground"
            width={{ xs: '95%', sm: '85%' }}
            spacing={1}
          />
        ))}
      </Stack>
      <Button
        onClick={event => {
          event.preventDefault()
          const newSubEvent = createUniqueDeepClone(
            defaultSubEvent(
              eventId === 'new' ? -1 : Number(eventId),
              sessionKey !== -1 ? [sessionId] : []
            )
          )
          appendSubEvent(newSubEvent)
          setShowSearch(false)

          // Update session with new subEvent added
          if (sessionKey !== -1) {
            setValue(`sessions.${sessionKey}.sub_events`, [
              ...getValues(`sessions.${sessionKey}.sub_events`),
              newSubEvent.id,
            ])
          }
        }}
        sx={{ width: '85%' }}
      >
        {t('Ajouter une intervention')}
      </Button>
    </Stack>
  )
}

export default function Step2DashboardView() {
  const { t } = useTranslation('events')
  const [searchParams, setSearchParams] = useSearchParams()
  const sessionIdParam = searchParams.get('sessionId')

  const tagsOptions = useTagsDataOptions()
  const sessionsTypeOptions = useSessionTypeOptions(t)
  const {
    sessionsFields: sessions,
    subEventsFields: subEvents,
    eventStart,
    eventEnd,
    removeSession,
    setShowSearch,
  } = useContext(SubEventsStep2Context)

  const [selectedSession, setSelectedSession] = useState<[number, Partial<Sessions>] | null>(null)

  useEffect(() => {
    setSelectedSession(
      Number(sessionIdParam) === -1
        ? [-1, {}]
        : sessions.reduce(
            (acc, [key, el]) => {
              if (acc) return acc
              if (Number(el.id) === Number(sessionIdParam)) {
                return [key, el]
              }
              return acc
            },
            null as [number, Partial<Sessions>] | null
          )
    )
  }, [sessionIdParam, sessions])

  return (
    <Stack spacing={1} alignItems="center">
      {selectedSession ? (
        <>
          <Stack direction="row" justifyContent="space-between" width="100%">
            <Button
              variant="contained"
              onClick={() => {
                setSelectedSession(null)
                searchParams.delete('sessionId')
                setSearchParams(searchParams)
                setShowSearch(false)
              }}
            >
              {t('Retour Dashboard')}
            </Button>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                disabled={selectedSession[0] === 0 || sessions.length === 0}
                onClick={() => {
                  if (selectedSession[0] === -1) {
                    const lastSession = sessions[sessions.length - 1]
                    setSelectedSession(lastSession)
                    searchParams.set('sessionId', lastSession[1].id.toString())
                    setSearchParams(searchParams)
                  } else {
                    const prevSession = sessions[selectedSession[0] - 1]
                    setSelectedSession(prevSession)
                    searchParams.set('sessionId', prevSession[1].id.toString())
                    setSearchParams(searchParams)
                  }
                  setShowSearch(false)
                }}
              >
                {t('Précédent')}
              </Button>
              <Button
                variant="contained"
                disabled={selectedSession[0] === sessions.length || selectedSession[0] === -1}
                onClick={() => {
                  if (selectedSession[0] === sessions.length - 1) {
                    setSelectedSession([-1, {}])
                    searchParams.set('sessionId', '-1')
                    setSearchParams(searchParams)
                  } else {
                    const nextSession = sessions[selectedSession[0] + 1]
                    setSelectedSession(nextSession)
                    searchParams.set('sessionId', nextSession[1].id.toString())
                    setSearchParams(searchParams)
                  }
                  setShowSearch(false)
                }}
              >
                {t('Suivant')}
              </Button>
            </Stack>
          </Stack>
          <InnerSessionList
            key={selectedSession[0]}
            id={selectedSession[1].id ?? -1}
            sessionKey={selectedSession[0]}
            minDateTime={eventStart ? new Date(eventStart) : undefined}
            maxDateTime={eventEnd ? new Date(eventEnd) : undefined}
            onRemove={() => {
              removeSession(selectedSession[0])
              setSelectedSession(null)
              searchParams.delete('sessionId')
              setSearchParams(searchParams)
            }}
          />
        </>
      ) : (
        <Stack spacing={1} alignItems="center" width="100%">
          <h3>{t('Liste des séances')}</h3>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} width="100%">
            {sessions.map(([key, session]) => (
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }} key={session.id}>
                <Card
                  onClick={() => {
                    setSelectedSession([key, session])
                    searchParams.set('sessionId', session.id.toString())
                    setSearchParams(searchParams)
                    setShowSearch(false)
                  }}
                >
                  <Stack justifyContent={'center'} gap={0.5}>
                    <Stack
                      direction={'row'}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                      gap={1}
                    >
                      <StatusTag
                        color="#D9D9D9"
                        size="small"
                        position="top"
                        absolute={false}
                        sx={{ flex: 1 }}
                      >
                        ID {session.id}
                      </StatusTag>
                      <StatusTag
                        color={session.color}
                        size="medium"
                        position="top"
                        absolute={false}
                        sx={{ flex: 3 }}
                      >
                        {session.name}
                      </StatusTag>
                      <Stack direction={'column'} alignItems={'center'} gap={1} sx={{ flex: 1 }}>
                        {session.tags.map(tag => {
                          const dataTag = tagsOptions.find(el => el.value === tag)
                          return (
                            <StatusTag
                              key={tag}
                              color={dataTag?.color ?? '#D9D9D9'}
                              size="small"
                              position="top"
                              absolute={false}
                            >
                              {dataTag?.label ?? t('NC')}
                            </StatusTag>
                          )
                        })}
                      </Stack>
                    </Stack>
                    <Stack
                      direction={'row'}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                      gap={1}
                    >
                      <Span bold>
                        {t('Type')}:{' '}
                        {sessionsTypeOptions.find(el => el.value === session.type)?.label}
                      </Span>
                    </Stack>
                    <Stack
                      direction={'row'}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                      gap={1}
                    >
                      <Span
                        bold
                      >{`${t('Début')}: ${new Date(session.start).toLocaleString()}`}</Span>
                      <Span bold>{`${t('Fin')}: ${new Date(session.end).toLocaleString()}`}</Span>
                    </Stack>

                    <Span
                      style={{ textAlign: 'center' }}
                    >{`${t("Nombre d'interventions")}: ${session.sub_events?.length}`}</Span>
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Divider flexItem variant="fullWidth" />
          <Card
            onClick={() => {
              setSelectedSession([-1, {}])
              searchParams.set('sessionId', '-1')
              setSearchParams(searchParams)
              setShowSearch(false)
            }}
          >
            <h4>{t('Interventions sans séance')}</h4>
            <Span
              style={{ textAlign: 'center' }}
            >{`${t("Nombre d'interventions")}: ${subEvents.filter(el => !el[1].sessions.length).length}`}</Span>
          </Card>
        </Stack>
      )}
    </Stack>
  )
}
